import React, {useEffect, useState} from "react";
import "./App.css";
import styles, {C} from "./styles";
import PseudoWaitlistNotice from "./components/PseudoWaitlistNotice";
import {generateAvatar, getAnonymousAvatar, validatePassword} from "./utils";
import {trackSignupSucceeded} from "./analytics";
import {useAuth} from "./hooks/useAuth";
import useIsMobile from "./hooks/useIsMobile";
import {useMoodToday} from "./hooks/useMoodToday";
import {apiConfig, apiResetPassword, apiGetPseudoSuggestions, apiVerifyEmail, apiConfirmEmailChange, apiResendVerification} from "./api/auth";
import {apiDeleteGroup, apiGetGroups} from "./api/groups";
import {apiGetPosts} from "./api/posts";
import {apiGetSurveys} from "./api/surveys";
import {apiGetAllCommunities} from "./api/communities";
import {apiGetReports} from "./api/reports";
import {apiGetAllHelpRequests} from "./api/help";
import SurveysPage from "./pages/SurveysPage";
import IdeasPage from "./pages/IdeasPage";
import DiscussionsPage from "./pages/DiscussionsPage";
import ProfilePage from "./pages/ProfilePage";
import ReportsPage from "./pages/ReportsPage";
import AidePage from "./pages/AidePage";
import AdminPage from "./pages/AdminPage";
import Survey from "./components/Survey";
import MoodWidget from "./components/MoodWidget";
import DailyMoodPopup from "./components/DailyMoodPopup";
import HelpButton from "./components/HelpButton";
import EmailVerificationNotice from "./components/EmailVerificationNotice";
import ForcedPasswordChangeModal from "./components/ForcedPasswordChangeModal";
import ForgotAccountModal from "./components/ForgotAccountModal";
import InviteColleagueButton from "./components/InviteColleagueButton";
import LandingPage from "./LandingPage";
import InvitePage from "./pages/InvitePage";
import CompleteRegistrationPage from "./pages/CompleteRegistrationPage";
import QvtPage from "./pages/QvtPage";
import Ghq12Page from "./pages/Ghq12Page";
import IaAgentsPage from "./pages/IaAgentsPage";
import SappmPage from "./pages/SappmPage";
import ConfidentialitePage from "./pages/ConfidentialitePage";
import MentionsPage from "./pages/MentionsPage";
import ContactPage from "./pages/ContactPage";
import NewsListPage from "./pages/NewsListPage";
import NewsArticlePage from "./pages/NewsArticlePage";
import {useToast, useConfirm, usePrompt} from "./components/FeedbackProvider";
import CguPage from "./pages/CguPage";
import CguAcceptance from "./components/CguAcceptance";
import AboutModal from "./components/AboutModal";

function getCurrentPage() {
    const path = window.location.pathname;
    if (path === "/") return "landing";
    if (path.startsWith("/reset-password/")) return "reset-password";
    if (path.startsWith("/verify-email/")) return "verify-email";
    if (path.startsWith("/confirm-email-change/")) return "confirm-email-change";
    if (path.startsWith("/complete-registration/")) return "complete-registration";
    if (path.startsWith("/invite/")) return "invite";
    if (path === "/qvt") return "qvt";
    if (path === "/ghq12") return "ghq12";
    if (path === "/ia-agents") return "ia-agents";
    if (path === "/sappm") return "sappm";
    if (path === "/confidentialite") return "confidentialite";
    if (path === "/mentions") return "mentions";
    if (path === "/cgu") return "cgu";
    if (path === "/contact") return "contact";
    if (path === "/actualites") return "actualites";
    if (path.startsWith("/actualites/")) return "actualites-detail";
    return "app";
}

export default function App() {
    const {notify} = useToast();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const {user, setUser, login, logout, register, initFromToken} = useAuth();
    const [config, setConfig] = useState({answers: [], min_members_for_results: 15, min_votes_for_results: 5, cgu_version: null});
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const moodToday = useMoodToday(selectedGroup?.id);
    const [surveys, setSurveys] = useState([]);
    const [posts, setPosts] = useState([]);
    const [allCommunities, setAllCommunities] = useState([]);
    const [reports, setReports] = useState([]);
    const [helpRequests, setHelpRequests] = useState([]);
    const [activeTab, setActiveTab] = useState("accueil");
    const [authMode, setAuthMode] = useState("login");
    const [forgotModalType, setForgotModalType] = useState(null);
    const [groupOrder, setGroupOrder] = useState(() => {
        try { return JSON.parse(localStorage.getItem("groupOrder") || "[]"); } catch { return []; }
    });
    const [sidebarOpen, setSidebarOpen] = useState(
        () => localStorage.getItem("sidebarOpen") !== "false"
    );
    const [authMessage, setAuthMessage] = useState("");
    const [pendingVerification, setPendingVerification] = useState(null);
    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [resetPassword, setResetPassword] = useState("");
    const [verifyEmailStatus, setVerifyEmailStatus] = useState(null);
    const [showResendVerification, setShowResendVerification] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedPseudo, setSelectedPseudo] = useState(null);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [cguAccepted, setCguAccepted] = useState(false);
    const [showAbout, setShowAbout] = useState(false);
    const isAdmin = user?.role === "admin";
    const hasElevatedAccess = isAdmin || user?.role === "agent_sappm";
    // Le serveur renvoie member_count: null quand l'effectif est sous le seuil d'anonymat (sauf admin / agent SAPPM).
    const canSeeMemberCount = (group) => group.member_count != null;
    const isMobile = useIsMobile();
    const [currentPage, setCurrentPage] = useState(getCurrentPage);
    const resetToken = currentPage === "reset-password"
        ? window.location.pathname.split("/")[2]
        : null;
    // Même écran pour la vérification d'inscription et la confirmation d'un changement d'email pro.
    const verifyEmailToken = (currentPage === "verify-email" || currentPage === "confirm-email-change")
        ? window.location.pathname.split("/")[2]
        : null;
    const isEmailChange = currentPage === "confirm-email-change";
    const inviteToken = currentPage === "invite"
        ? window.location.pathname.split("/")[2]
        : null;
    const completeRegistrationToken = currentPage === "complete-registration"
        ? window.location.pathname.split("/")[2]
        : null;
    const newsSlug = currentPage === "actualites-detail"
        ? window.location.pathname.split("/")[2]
        : null;

    function goToApp() {
        window.history.pushState({}, "", "/app");
        setCurrentPage("app");
    }

    function goToRegister() {
        window.history.pushState({}, "", "/app");
        setCurrentPage("app");
        setAuthMode("register");
    }

    useEffect(() => {
        apiConfig().then(setConfig);
        initFromToken();
        loadGroups();
        loadAllCommunities();
        function handlePopState() { setCurrentPage(getCurrentPage()); }
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedGroup) {
            loadSurveys(selectedGroup.id);
            loadPosts(selectedGroup.id);
            loadAllCommunities();
            if (hasElevatedAccess) loadReports();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGroup]);

    useEffect(() => {
        if (authMode === "register") {
            setLoadingSuggestions(true);
            setSelectedPseudo(null);
            setSuggestions([]);
            setConfirmPassword("");
            setCguAccepted(false);
            apiGetPseudoSuggestions().then(data => {
                setSuggestions(Array.isArray(data) ? data : []);
                setLoadingSuggestions(false);
            });
        }
    }, [authMode]);

    useEffect(() => {
        if (verifyEmailToken) {
            (isEmailChange ? apiConfirmEmailChange : apiVerifyEmail)(verifyEmailToken).then(({ok, data}) => {
                setVerifyEmailStatus({ok, message: data.message || data.error});
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifyEmailToken]);

    function loadGroups() {
        apiGetGroups().then(data => {
            setGroups(data);
            if (data.length > 0 && !selectedGroup) setSelectedGroup(data[0]);
        });
    }

    function loadSurveys(groupId) {
        apiGetSurveys(groupId).then(data => setSurveys(Array.isArray(data) ? data : []));
    }

    function loadPosts(groupId) {
        apiGetPosts(groupId).then(data => setPosts(Array.isArray(data) ? data : []));
    }

    function loadAllCommunities() {
        apiGetAllCommunities().then(data => setAllCommunities(Array.isArray(data) ? data : []));
    }

    function loadReports() {
        apiGetReports().then(data => setReports(Array.isArray(data) ? data : []));
    }

    function loadHelpRequestsAdmin() {
        apiGetAllHelpRequests().then(data => setHelpRequests(Array.isArray(data) ? data : []));
    }


    function refreshAll() {
        if (selectedGroup) {
            loadSurveys(selectedGroup.id);
            loadPosts(selectedGroup.id);
            if (hasElevatedAccess) loadReports();
        }
        loadAllCommunities();
        loadGroups();
    }

    async function handleLogin() {
        setAuthMessage("");
        setShowResendVerification(false);
        setPendingVerification(null);
        const result = await login(username, password);
        if (result) {
            setAuthMessage(result.error);
            setShowResendVerification(result.code === "EMAIL_NOT_VERIFIED");
        } else {
            refreshAll();
        }
    }

    function toggleAuthMode() {
        setPendingVerification(null);
        setAuthMode(authMode === "login" ? "register" : "login");
    }

    async function handleResendVerification() {
        const emailForResend = await prompt("Entrez votre adresse email professionnelle");
        if (!emailForResend) return;
        const data = await apiResendVerification(emailForResend);
        notify(data.message || "Si ce compte existe et n'est pas encore vérifié, un email a été envoyé.", "success");
    }

    function refreshSuggestions() {
        setLoadingSuggestions(true);
        setSelectedPseudo(null);
        apiGetPseudoSuggestions().then(data => {
            setSuggestions(Array.isArray(data) ? data : []);
            setLoadingSuggestions(false);
        });
    }

    async function handleRegister() {
        setAuthMessage("");
        const error = await register(email, selectedPseudo.pseudo, password, cguAccepted);
        if (error) {
            setAuthMessage(error);
            return;
        }
        trackSignupSucceeded();
        setUsername(selectedPseudo.pseudo);
        setPendingVerification({email: email.trim(), pseudo: selectedPseudo.pseudo});
        setAuthMode("login");
    }

    async function handleDeleteGroup(groupId) {
        if (!(await confirm("Supprimer ce collectif ? Cette action est définitive.", {danger: true}))) return;
        await apiDeleteGroup(groupId);
        setSelectedGroup(null);
        loadGroups();
    }

    async function handleResetPassword() {
        const {ok, data} = await apiResetPassword(resetToken, resetPassword);
        if (ok) {
            notify("Mot de passe réinitialisé. Vous pouvez vous connecter.", "success");
            window.history.pushState({}, "", "/app");
            setCurrentPage("app");
        } else notify(data.error || "Erreur", "error");
    }

    const avatarData = user ? generateAvatar(user.username) : null;

    const sortedGroups = [...groups].sort((a, b) => {
        const ia = groupOrder.indexOf(a.id);
        const ib = groupOrder.indexOf(b.id);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });

    function moveGroup(id, direction) {
        const ids = sortedGroups.map(g => g.id);
        const idx = ids.indexOf(id);
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= ids.length) return;
        [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
        setGroupOrder(ids);
        localStorage.setItem("groupOrder", JSON.stringify(ids));
    }

    if (currentPage === "landing") {
        return <LandingPage user={user} onGoToApp={goToApp} onRegister={goToRegister}/>;
    }

    if (currentPage === "qvt") {
        return <QvtPage onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "ghq12") {
        return <Ghq12Page onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "ia-agents") {
        return <IaAgentsPage onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "sappm") {
        return <SappmPage onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "confidentialite") {
        return <ConfidentialitePage onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "mentions") {
        return <MentionsPage onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "cgu") {
        return <CguPage cguVersion={config.cgu_version} onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "contact") {
        return <ContactPage onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "actualites") {
        return <NewsListPage onBack={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }}/>;
    }

    if (currentPage === "actualites-detail") {
        return (
            <NewsArticlePage
                slug={newsSlug}
                onBack={() => { window.history.pushState({}, "", "/actualites"); setCurrentPage("actualites"); }}
            />
        );
    }

    if (resetToken) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <h2 style={{fontFamily: "'Fraunces', serif"}}>🔐 Réinitialiser mon mot de passe</h2>
                    <input type="password" placeholder="Nouveau mot de passe" value={resetPassword}
                           onChange={e => setResetPassword(e.target.value)} style={styles.input}/>
                    <button onClick={handleResetPassword} style={styles.button}>Valider le nouveau mot de passe</button>
                </div>
            </div>
        );
    }

    if (verifyEmailToken) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <h2 style={{fontFamily: "'Fraunces', serif"}}>✅ Vérification de l'email</h2>
                    {!verifyEmailStatus && <p>Vérification en cours…</p>}
                    {verifyEmailStatus && (
                        <>
                            <p>{verifyEmailStatus.message}</p>
                            {verifyEmailStatus.ok && (
                                <button onClick={() => { window.history.pushState({}, "", "/app"); setCurrentPage("app"); }} style={styles.button}>
                                    {isEmailChange ? "Aller à l'application" : "Aller à la connexion"}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (inviteToken) {
        return <InvitePage
            token={inviteToken}
            user={user}
            login={login}
            register={register}
            onGoToApp={goToApp}
            onRefreshGroups={loadGroups}
        />;
    }

    if (completeRegistrationToken) {
        return <CompleteRegistrationPage token={completeRegistrationToken} onGoToLogin={goToApp} />;
    }

    function tabStyle(tab) {
        const active = activeTab === tab;
        return {
            background: "none",
            border: "none",
            borderBottom: active ? `2px solid ${C.primary}` : "2px solid transparent",
            color: active ? C.primary : C.textMuted,
            fontWeight: active ? 700 : 500,
            fontSize: 14,
            padding: "10px 14px",
            cursor: "pointer",
            marginBottom: -2,
            transition: "color 0.15s, border-color 0.15s",
            whiteSpace: "nowrap",
        };
    }

    /* Onglet Accueil : sondages et idées alternés dans un seul fil */
    function buildAccueilFeed() {
        const feed = [];
        const maxLen = Math.max(surveys.length, posts.length);
        for (let i = 0; i < maxLen; i++) {
            if (surveys[i]) feed.push({type: "survey", data: surveys[i]});
            if (posts[i]) feed.push({type: "post", data: posts[i]});
        }
        return feed;
    }

    const GROUP_COLORS = {
        "SPM":   { base: "#0F6A71", light: "#1DAAB8", dark: "#064446" },
        "DILA":  { base: "#1A5BA8", light: "#2E82D4", dark: "#0E3668" },
        "INSP":  { base: "#A85E15", light: "#D4821E", dark: "#6B3B0D" },
        "SGDSN": { base: "#3D5A7C", light: "#5880AA", dark: "#22374E" },
    };

    const pwdCheck = validatePassword(password);
    const validCount = [pwdCheck.minLength, pwdCheck.hasUpper, pwdCheck.hasDigit, pwdCheck.hasSpecial].filter(Boolean).length;
    const strengthColor = validCount === 0 ? C.border : ["#EF4444", "#F97316", "#EAB308", "#22C55E"][validCount - 1];
    const canRegister = !!(email && selectedPseudo && pwdCheck.valid && confirmPassword.length > 0 && password === confirmPassword && cguAccepted);

    return (
        <div style={styles.page}>
            {/* HEADER */}
            <div style={styles.header}>
                {/* Gauche : logo + titre + slogan */}
                <div style={styles.logoZone}>
                    <img src="/paroles-agents-logo.png" alt="SAPPM" style={{height: 48, width: "auto", borderRadius: 10, border: "1.5px solid rgba(255,255,255,0.25)"}}/>
                    <div>
                        <h1 style={{margin: 0, fontSize: 22, lineHeight: "26px", fontWeight: 600, fontFamily: "'Fraunces', serif"}}>Paroles d'Agents</h1>
                        <div style={{fontSize: 13, marginTop: 3, fontStyle: "italic", opacity: 0.9}}>
                            Pour que chacun s'exprime
                        </div>
                        <svg width="60" height="8" viewBox="0 0 60 8" style={{marginTop: 2}}>
                            <path d="M2 5 Q15 1, 30 4 T58 3" stroke={C.accent} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                        </svg>
                    </div>
                </div>

                {/* Centre : phrase de mission (masqué sur très petit écran via CSS) */}
                <div className="header-mission" style={{flex: 1, textAlign: "center", padding: "0 16px"}}>
                    <div style={{fontSize: 17, fontWeight: 700, letterSpacing: 0.3}}>
                        💬 Réseau d'échange, de propositions et d'alertes
                    </div>
                    <div style={{fontSize: 13, marginTop: 4, opacity: 0.85, fontStyle: "italic"}}>
                        Ensemble, faisons entendre la voix des agents
                    </div>
                </div>

                {/* Droite : retour site (web seulement) + profil */}
                <div style={{display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "flex-end"}}>
                    <button onClick={() => { window.history.pushState({}, "", "/"); setCurrentPage("landing"); }} style={{
                        background: "rgba(255,255,255,0.15)",
                        border: "1px solid rgba(255,255,255,0.3)",
                        borderRadius: 50,
                        padding: "8px 18px",
                        fontSize: 13,
                        color: "#fff",
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}>← Retour à la page d'accueil</button>
                    {user && (
                        <div style={{display: "flex", alignItems: "center", gap: 12}}>
                            <InviteColleagueButton selectedGroup={selectedGroup} />
                            <HelpButton
                                hasUnreadReply={!!user.has_unread_help_reply}
                                onRepliesRead={() => setUser(u => ({...u, has_unread_help_reply: false}))}
                            />
                            <div style={{textAlign: "right"}}>
                                <strong>{user.username}</strong><br/>
                                <small>{isAdmin ? "ADMIN SAPPM" : "Agent"}</small>
                            </div>
                            <div style={{...styles.avatarCircle, backgroundColor: avatarData.color}}>
                                {avatarData.avatar}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div style={styles.layout}>
                {/* SIDEBAR */}
                {!isMobile && !sidebarOpen && (
                    <button onClick={() => { setSidebarOpen(true); localStorage.setItem("sidebarOpen", "true"); }} style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                        background: C.primary,
                        color: "white",
                        border: "none",
                        borderRadius: 10,
                        padding: "12px 8px",
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 600,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                        alignSelf: "flex-start",
                    }}>{"Collectifs ›".split("").map((char, i) => (
                        <span key={i} style={{lineHeight: 1}}>{char === " " ? " " : char}</span>
                    ))}</button>
                )}
                {!isMobile && sidebarOpen && (
                    <div style={styles.sidebar}>
                        <button onClick={() => { setSidebarOpen(false); localStorage.setItem("sidebarOpen", "false"); }} style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            width: "100%", marginBottom: 12, background: "none", border: "none",
                            cursor: "pointer", padding: "6px 0", borderRadius: 6,
                        }} title="Réduire">
                            <h3 style={{margin: 0, color: C.text, fontFamily: "'Fraunces', serif"}}>Collectifs</h3>
                            <span style={{color: C.textMuted, fontSize: 18, lineHeight: 1}}>‹</span>
                        </button>
                        <div style={{
                            fontSize: 12,
                            color: C.textMuted,
                            padding: "6px 10px",
                            backgroundColor: C.primaryLight,
                            borderRadius: 8,
                            lineHeight: 1.5,
                            marginBottom: 14,
                        }}>
                            ℹ️ Anonymat garanti · résultats des sondages visibles dès {config.min_members_for_results} agents concernés et {config.min_votes_for_results ?? 5} participations.
                        </div>
                        {sortedGroups.map((group, idx) => (
                            <div key={group.id} onClick={() => setSelectedGroup(group)}
                                 style={{
                                     padding: "10px 12px",
                                     marginBottom: 6,
                                     borderRadius: 8,
                                     cursor: "pointer",
                                     border: `1px solid ${selectedGroup?.id === group.id ? "#1E6B5E" : "#DEDED8"}`,
                                     backgroundColor: selectedGroup?.id === group.id ? "#E8F4F1" : "transparent",
                                     borderLeft: `3px solid ${selectedGroup?.id === group.id ? "#1E6B5E" : "#DEDED8"}`,
                                 }}>
                                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                    <strong style={{fontSize: 13}}>{group.name}</strong>
                                    <div style={{display: "flex", alignItems: "center", gap: 4}}>
                                        {canSeeMemberCount(group) && <span style={{
                                            backgroundColor: selectedGroup?.id === group.id ? C.primary : C.border,
                                            color: selectedGroup?.id === group.id ? "white" : C.textMuted,
                                            borderRadius: 20,
                                            padding: "2px 8px",
                                            fontSize: 11,
                                            fontWeight: 600,
                                        }}>{group.member_count || 0} ag.</span>}
                                        <button onClick={e => { e.stopPropagation(); moveGroup(group.id, -1); }}
                                            disabled={idx === 0}
                                            title="Monter"
                                            style={{background: "none", border: "none", cursor: idx === 0 ? "default" : "pointer", opacity: idx === 0 ? 0.3 : 1, padding: "0 2px", fontSize: 13}}>↑</button>
                                        <button onClick={e => { e.stopPropagation(); moveGroup(group.id, 1); }}
                                            disabled={idx === sortedGroups.length - 1}
                                            title="Descendre"
                                            style={{background: "none", border: "none", cursor: idx === sortedGroups.length - 1 ? "default" : "pointer", opacity: idx === sortedGroups.length - 1 ? 0.3 : 1, padding: "0 2px", fontSize: 13}}>↓</button>
                                    </div>
                                </div>
                                <div style={{fontSize: 12, color: C.textMuted, marginTop: 2}}>@{group.domain}</div>
                                {isAdmin && (
                                    <div style={{marginTop: 10}}>
                                        <button onClick={e => {
                                            e.stopPropagation();
                                            handleDeleteGroup(group.id);
                                        }} style={styles.dangerButton}>Supprimer</button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* MAIN */}
                <div style={{
                    ...styles.main,
                    width: isMobile ? "100%" : undefined,
                    paddingBottom: isMobile ? "calc(80px + env(safe-area-inset-bottom))" : undefined,
                }}>
                    {isMobile && (
                        <div
                            className="group-chips-scroll"
                            style={{
                                display: "flex",
                                gap: 8,
                                overflowX: "auto",
                                padding: "4px 2px",
                                marginBottom: 12,
                                scrollbarWidth: "none",
                            }}
                        >
                            {sortedGroups.map(group => {
                                const active = selectedGroup?.id === group.id;
                                const c = GROUP_COLORS[group.name] || { base: C.primary, light: "#1DAAB8", dark: "#064446" };
                                return (
                                    <button
                                        key={group.id}
                                        onClick={() => setSelectedGroup(group)}
                                        style={{
                                            background: `radial-gradient(circle at 32% 28%, ${c.light}, ${c.base} 55%, ${c.dark})`,
                                            color: "white",
                                            border: "none",
                                            borderRadius: 50,
                                            padding: "6px 11px",
                                            fontSize: 10,
                                            fontWeight: 800,
                                            letterSpacing: "0.8px",
                                            whiteSpace: "nowrap",
                                            cursor: "pointer",
                                            flexShrink: 0,
                                            minHeight: "auto",
                                            textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                                            boxShadow: active
                                                ? "2px 3px 8px rgba(0,0,0,0.38), inset 0 1px 3px rgba(255,255,255,0.55), inset 2px 2px 8px rgba(255,255,255,0.15)"
                                                : "1px 2px 5px rgba(0,0,0,0.22), inset 0 1px 2px rgba(255,255,255,0.4)",
                                            opacity: active ? 1 : 0.72,
                                            transform: active ? "scale(1.08)" : "scale(1)",
                                            transition: "transform 0.15s, box-shadow 0.15s, opacity 0.15s",
                                        }}
                                    >
                                        {group.name}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* TAB BAR — desktop uniquement */}
                    {!isMobile && (
                        <div style={styles.tabBar}>
                            <button onClick={() => setActiveTab("accueil")} style={tabStyle("accueil")}>🏠 Accueil</button>
                            <button onClick={() => setActiveTab("surveys")} style={tabStyle("surveys")}>📊 Sondages</button>
                            <button onClick={() => setActiveTab("ideas")} style={tabStyle("ideas")}>💡 Idées</button>
                            <button onClick={() => setActiveTab("discussions")} style={tabStyle("discussions")}>💬 Discussions</button>
                            <button onClick={() => setActiveTab("profile")} style={tabStyle("profile")}>👤 Profil</button>
                            {hasElevatedAccess && <button onClick={() => { setActiveTab("reports"); loadReports(); }} style={tabStyle("reports")}>🚩 Signalements</button>}
                            {hasElevatedAccess && <button onClick={() => { setActiveTab("aide"); loadHelpRequestsAdmin(); }} style={tabStyle("aide")}>🆘 Aide</button>}
                            {hasElevatedAccess && <button onClick={() => setActiveTab("admin")} style={tabStyle("admin")}>🛠 Admin</button>}
                        </div>
                    )}

                    {selectedGroup && (
                        <div style={{
                            fontSize: 12,
                            color: C.textMuted,
                            marginBottom: 16,
                            marginTop: 6,
                            paddingLeft: 2,
                        }}>
                            {selectedGroup.name} · @{selectedGroup.domain}{canSeeMemberCount(selectedGroup) && ` · ${selectedGroup.member_count || 0} agent(s)`}
                        </div>
                    )}

                    {/* AUTH */}
                    {!user && (
                        <div style={styles.card}>
                            <h2 style={{fontFamily: "'Fraunces', serif", marginTop: 0}}>
                                {authMode === "login" ? "Connexion" : "Créer un compte"}
                            </h2>

                            {authMode === "login" && pendingVerification && (
                                <EmailVerificationNotice
                                    email={pendingVerification.email}
                                    pseudo={pendingVerification.pseudo}
                                    nextStep="connectez-vous ci-dessous"
                                />
                            )}

                            {/* LOGIN */}
                            {authMode === "login" && (
                                <form onSubmit={e => { e.preventDefault(); handleLogin(); }}>
                                    <input value={username} onChange={e => setUsername(e.target.value)}
                                           placeholder="Pseudo" style={styles.input}/>
                                    <div style={{display: "flex", gap: 8}}>
                                        <input type={showPassword ? "text" : "password"} value={password}
                                               onChange={e => setPassword(e.target.value)} placeholder="Mot de passe"
                                               style={styles.input}/>
                                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                                                style={styles.button}>{showPassword ? "🙈" : "👁️"}</button>
                                    </div>
                                    <button type="submit" style={styles.button}>Se connecter</button>
                                    <button type="button" onClick={() => setForgotModalType("password")}
                                            style={{...styles.button, backgroundColor: "transparent", color: "#1E6B5E", border: "1px solid #1E6B5E"}}>
                                        Mot de passe oublié ?
                                    </button>
                                    <button type="button" onClick={() => setForgotModalType("pseudo")}
                                            style={{...styles.button, backgroundColor: "transparent", color: "#1E6B5E", border: "1px solid #1E6B5E"}}>
                                        Pseudo oublié ?
                                    </button>
                                    {showResendVerification && (
                                        <button type="button" onClick={handleResendVerification}
                                                style={{...styles.button, backgroundColor: "transparent", color: "#1E6B5E", border: "1px solid #1E6B5E"}}>
                                            Renvoyer l'email de vérification
                                        </button>
                                    )}
                                </form>
                            )}

                            {/* REGISTER */}
                            {authMode === "register" && (
                                <>
                                    <input value={email} onChange={e => setEmail(e.target.value)}
                                           placeholder="Email professionnel" style={styles.input}/>

                                    <p style={{fontSize: 13, color: C.textMuted, margin: "0 0 8px"}}>
                                        Choisissez votre pseudo — il préserve votre anonymat
                                    </p>
                                    {loadingSuggestions ? (
                                        <p style={{fontSize: 13, color: C.textMuted, marginBottom: 12}}>Génération des suggestions…</p>
                                    ) : suggestions.length === 0 ? (
                                        <PseudoWaitlistNotice email={email} onJoined={() => {}} />
                                    ) : (
                                        <>
                                            <div style={{display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap"}}>
                                                {suggestions.map(s => (
                                                    <button
                                                        key={s.pseudo}
                                                        type="button"
                                                        onClick={() => setSelectedPseudo(s)}
                                                        style={{
                                                            flex: "1 1 120px",
                                                            padding: "12px 8px",
                                                            borderRadius: 12,
                                                            border: selectedPseudo?.pseudo === s.pseudo
                                                                ? `2px solid ${C.primary}`
                                                                : `1px solid ${C.border}`,
                                                            background: `radial-gradient(circle at 35% 30%, ${s.couleur}CC, ${s.couleur}, ${s.couleur}88)`,
                                                            color: "white",
                                                            cursor: "pointer",
                                                            textAlign: "center",
                                                            transform: selectedPseudo?.pseudo === s.pseudo ? "scale(1.04)" : "scale(1)",
                                                            transition: "transform 0.15s, border-color 0.15s",
                                                            minHeight: "auto",
                                                            boxShadow: selectedPseudo?.pseudo === s.pseudo
                                                                ? `0 0 0 3px ${C.primaryLight}`
                                                                : "0 2px 6px rgba(0,0,0,0.12)",
                                                        }}
                                                    >
                                                        <div style={{fontSize: 28, marginBottom: 4}}>{s.emoji}</div>
                                                        <div style={{fontSize: 13, fontWeight: 700, textShadow: "0 1px 2px rgba(0,0,0,0.3)"}}>{s.pseudo}</div>
                                                    </button>
                                                ))}
                                            </div>
                                            <button type="button" onClick={refreshSuggestions}
                                                    style={{...styles.button, fontSize: 12, marginBottom: 12}}>
                                                🔄 Autres suggestions
                                            </button>
                                        </>
                                    )}

                                    {suggestions.length > 0 && (
                                        <>
                                            <div style={{display: "flex", gap: 8}}>
                                                <input type={showPassword ? "text" : "password"} value={password}
                                                       onChange={e => setPassword(e.target.value)} placeholder="Mot de passe"
                                                       style={styles.input}/>
                                                <button type="button" onClick={() => setShowPassword(!showPassword)}
                                                        style={styles.button}>{showPassword ? "🙈" : "👁️"}</button>
                                            </div>
                                            {password.length > 0 && (
                                                <>
                                                    <div style={{height: 4, borderRadius: 4, backgroundColor: C.border, marginBottom: 6, overflow: "hidden"}}>
                                                        <div style={{
                                                            height: "100%",
                                                            width: `${(validCount / 4) * 100}%`,
                                                            backgroundColor: strengthColor,
                                                            transition: "width 0.2s, background-color 0.2s",
                                                            borderRadius: 4,
                                                        }}/>
                                                    </div>
                                                    <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 12px", fontSize: 12, marginBottom: 10}}>
                                                        {[
                                                            [pwdCheck.minLength, "8 caractères min."],
                                                            [pwdCheck.hasUpper, "1 majuscule"],
                                                            [pwdCheck.hasDigit, "1 chiffre"],
                                                            [pwdCheck.hasSpecial, "1 caractère spécial"],
                                                        ].map(([ok, label]) => (
                                                            <span key={label} style={{color: ok ? "#22C55E" : C.textMuted}}>
                                                                {ok ? "✅" : "○"} {label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </>
                                            )}

                                            <div style={{display: "flex", gap: 8}}>
                                                <input type={showConfirmPassword ? "text" : "password"} value={confirmPassword}
                                                       onChange={e => setConfirmPassword(e.target.value)}
                                                       placeholder="Confirmer le mot de passe"
                                                       style={styles.input}/>
                                                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        style={styles.button}>{showConfirmPassword ? "🙈" : "👁️"}</button>
                                            </div>
                                            {confirmPassword.length > 0 && password !== confirmPassword && (
                                                <p style={{margin: "0 0 8px", fontSize: 12, color: "#EF4444"}}>
                                                    Les mots de passe ne correspondent pas
                                                </p>
                                            )}

                                            <CguAcceptance accepted={cguAccepted} onChange={setCguAccepted}/>

                                            <button onClick={handleRegister} disabled={!canRegister}
                                                    style={{...styles.button, opacity: canRegister ? 1 : 0.5, cursor: canRegister ? "pointer" : "not-allowed"}}>
                                                Créer mon compte
                                            </button>
                                        </>
                                    )}
                                </>
                            )}

                            <button onClick={toggleAuthMode} style={styles.button}>
                                {authMode === "login" ? "Créer un compte" : "Déjà inscrit ?"}
                            </button>
                            {authMessage && <p>{authMessage}</p>}
                        </div>
                    )}

                    {forgotModalType && (
                        <ForgotAccountModal type={forgotModalType} onClose={() => setForgotModalType(null)}/>
                    )}

                    {/* USER BAR */}
                    {user && (
                        <div style={{...styles.card, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap"}}>
                            <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                                <div><span>Connecté :</span> <strong>{user.username}</strong></div>
                                {isAdmin && <span style={{
                                    display: "inline-block",
                                    padding: "2px 8px",
                                    backgroundColor: "#E8F4F1",
                                    color: "#1E6B5E",
                                    borderRadius: 4,
                                    fontWeight: "600",
                                    fontSize: 11,
                                    border: "1px solid #1E6B5E",
                                    width: "fit-content",
                                    letterSpacing: 0.5,
                                }}>ADMIN SAPPM</span>}
                            </div>
                            <button onClick={logout} style={styles.button}>Déconnexion</button>
                        </div>
                    )}

                    {user && user.must_change_password && (
                        <ForcedPasswordChangeModal onSuccess={() => setUser(u => ({...u, must_change_password: false}))}/>
                    )}

                    {user && selectedGroup && (
                        <DailyMoodPopup userId={user.id} data={moodToday.data} vote={moodToday.vote}/>
                    )}


                    {/* PAGES */}
                    {activeTab === "accueil" && (
                        <>
                            {selectedGroup && <MoodWidget data={moodToday.data} submitting={moodToday.submitting}
                                                           justVoted={moodToday.justVoted} voteError={moodToday.voteError}
                                                           vote={moodToday.vote}/>}
                            {buildAccueilFeed().length === 0 && (
                                <p style={{color: "#6B7280", fontSize: 14, textAlign: "center", marginTop: 24}}>
                                    Aucun contenu pour ce collectif. Les sondages et idées publiés apparaîtront ici.
                                </p>
                            )}
                            {buildAccueilFeed().map((item, idx) => {
                                if (item.type === "survey") {
                                    return <Survey key={`s-${item.data.id}`} survey={item.data} user={user} answers={config.answers}/>;
                                }
                                return (
                                    <div key={`p-${item.data.id}`} style={{...styles.card, borderLeft: "4px solid #F0A500"}}>
                                        <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 6}}>
                                            <span style={{fontSize: 18}}>💡</span>
                                            <strong style={{fontSize: 13, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5}}>Idée du jour</strong>
                                        </div>
                                        <p style={{fontSize: 15, lineHeight: "22px"}}>{item.data.content}</p>
                                        <div style={{display: "flex", alignItems: "center", gap: 8, marginTop: 8}}>
                                            {item.data.is_anonymous === 1 && (() => {
                                                const av = getAnonymousAvatar(item.data);
                                                return (
                                                    <div style={{
                                                        width: 28, height: 28, borderRadius: "50%",
                                                        backgroundColor: av.couleur,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        fontSize: 16, flexShrink: 0,
                                                    }}>{av.emoji}</div>
                                                );
                                            })()}
                                            <small style={{color: "#6c8b74"}}>— {item.data.author}</small>
                                            {item.data.is_anonymous === 1 && (
                                                <span style={{fontSize: 11, fontWeight: 600, backgroundColor: "#F3F4F6", color: "#6B7280", border: "1px solid #D1D5DB", borderRadius: 20, padding: "1px 8px"}}>
                                                    Anonyme
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}

                    {activeTab === "surveys" && (
                        <SurveysPage surveys={surveys} user={user} selectedGroup={selectedGroup}
                                     groups={groups} answers={config.answers}
                                     onRefresh={() => loadSurveys(selectedGroup?.id)}/>
                    )}
                    {activeTab === "ideas" && (
                        <IdeasPage posts={posts} user={user} selectedGroup={selectedGroup}
                                   onRefresh={() => loadPosts(selectedGroup?.id)}/>
                    )}
                    {activeTab === "discussions" && (
                        <DiscussionsPage communities={allCommunities} user={user} selectedGroup={selectedGroup}
                                         onRefresh={() => loadAllCommunities(selectedGroup?.id)}/>
                    )}
                    {activeTab === "profile" && user && <ProfilePage user={user} onUserUpdate={setUser}/>}
                    {activeTab === "reports" && hasElevatedAccess &&
                        <ReportsPage reports={reports} selectedGroup={selectedGroup} onRefresh={refreshAll}/>}
                    {activeTab === "aide" && hasElevatedAccess &&
                        <AidePage helpRequests={helpRequests} onRefresh={loadHelpRequestsAdmin}/>}
                    {activeTab === "admin" && hasElevatedAccess &&
                        <AdminPage user={user} selectedGroup={selectedGroup} groups={groups} onRefresh={refreshAll} answers={config.answers}/>}

                    {/* FOOTER — lien discret vers les infos de version */}
                    <div style={{textAlign: "center", marginTop: 24, paddingTop: 12, borderTop: `1px solid ${C.border}`}}>
                        <button onClick={() => setShowAbout(true)} style={{
                            background: "none", border: "none", cursor: "pointer",
                            fontSize: 12, color: C.textMuted, textDecoration: "underline",
                        }}>À propos</button>
                    </div>
                    {showAbout && <AboutModal onClose={() => setShowAbout(false)}/>}

                    {/* BOTTOM NAV — mobile uniquement */}
                    {isMobile && (
                        <div style={styles.bottomNav}>
                            {[
                                {tab: "accueil", emoji: "🏠", label: "Accueil"},
                                {tab: "surveys", emoji: "📊", label: "Sondages"},
                                {tab: "ideas", emoji: "💡", label: "Idées"},
                                {tab: "discussions", emoji: "💬", label: "Discuter"},
                                {tab: "profile", emoji: "👤", label: "Profil"},
                                ...(hasElevatedAccess ? [{tab: "admin", emoji: "🛠", label: "Admin"}] : []),
                            ].map(({tab, emoji, label}) => (
                                <button
                                    key={tab}
                                    onClick={() => { setActiveTab(tab); if (tab === "reports") loadReports(); }}
                                    style={{
                                        ...styles.bottomNavItem,
                                        ...(activeTab === tab ? styles.bottomNavItemActive : {}),
                                    }}
                                >
                                    <span style={styles.bottomNavEmoji}>{emoji}</span>
                                    <span>{label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
